import React, { useCallback, useEffect, useState } from 'react';
import { useNotify, usePermissions, useRecordContext, useRefresh, useTranslate } from 'react-admin';
import {
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControlLabel,
    Radio,
    RadioGroup,
    TextField,
    Typography,
} from '@mui/material';
import { CreditCardOff } from '@mui/icons-material';
import { apiClient, type ApiError } from '../../utils/apiClient';
import { API_CONFIG } from '../../config/api';
import { hasPermission } from '../../utils/hasPermission';

export interface BlockedDeduct {
    attemptId: string;
    quoteId: string;
    amount: number;
    receiptNumber: string;
    startedAt: string;
    transactionId: string | null;
}

export interface OctopusClear {
    id: string;
    status: 'pending' | 'in_progress' | 'completed' | 'cancelled' | 'expired';
    createdAt: string;
    completedAt: string | null;
    attemptIds: string[];
    finding: 'not_charged' | 'charged';
    note: string;
    requestedBy: string;
    requestedByEmail: string;
    cleared: (BlockedDeduct & { clearedAt: string })[] | null;
    skipped: { attemptId: string; reason: 'not_found' | 'not_blocked' }[] | null;
}

const CLEAR_STALE_AFTER_MS = 24 * 60 * 60 * 1000;

const when = (iso: string) => new Date(iso).toLocaleString();

export const OctopusBlockedPanel = () => {
    const record = useRecordContext();
    const translate = useTranslate();
    const notify = useNotify();
    const refresh = useRefresh();
    const { permissions } = usePermissions();
    const [clears, setClears] = useState<OctopusClear[]>([]);
    const [open, setOpen] = useState(false);
    const [selected, setSelected] = useState<string[]>([]);
    const [finding, setFinding] = useState<'not_charged' | 'charged' | ''>('');
    const [note, setNote] = useState('');
    const [saving, setSaving] = useState(false);
    const [cancelling, setCancelling] = useState(false);

    const kioskId = record ? String(record.id) : null;
    const base = `${API_CONFIG.endpoints.backend.kioskDevices}/${kioskId}/octopus-clears`;

    const load = useCallback(async () => {
        if (kioskId === null) return;
        const body = await apiClient.get<{ data: OctopusClear[] }>(base);
        setClears(body.data);
    }, [kioskId, base]);

    const reload = useCallback(() => {
        load().catch((error: ApiError) => notify(translate('resources.kiosk-devices.octopusClear.loadFailed', { _: error.message }), { type: 'error' }));
    }, [load, notify, translate]);

    useEffect(() => {
        reload();
    }, [reload]);

    if (!record || kioskId === null) return null;
    const pending = clears.find((clear) => clear.status === 'pending' || clear.status === 'in_progress');
    const blocking = pending !== undefined && Date.now() - new Date(pending.createdAt).getTime() < CLEAR_STALE_AFTER_MS;
    const reported = record.octopusBlockedDeducts as BlockedDeduct[] | null;
    const rows: BlockedDeduct[] = reported === null ? [] : reported;
    if (rows.length === 0 && clears.length === 0) return null;
    const canWrite = hasPermission(permissions as string[] | null | undefined, 'kiosk-transactions:write:all');
    const canClear = canWrite && rows.length > 0 && !blocking;

    const start = () => {
        setSelected(rows.map((row) => row.attemptId));
        setFinding('');
        setNote('');
        setOpen(true);
    };

    const submit = async () => {
        setSaving(true);
        try {
            await apiClient.post(base, { attemptIds: selected, finding, note: note.trim() });
            notify(translate('resources.kiosk-devices.octopusClear.sent'), { type: 'success' });
            setOpen(false);
            await load();
            refresh();
        } catch (error) {
            const apiError = error as ApiError;
            notify(translate(`resources.kiosk-devices.errors.${apiError.errorCode}`, { _: translate('resources.kiosk-devices.octopusClear.failed') }), { type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const cancel = async (clear: OctopusClear) => {
        setCancelling(true);
        try {
            await apiClient.post(`${base}/${clear.id}/cancel`);
            notify(translate('resources.kiosk-devices.octopusClear.cancelled'), { type: 'success' });
            await load();
            refresh();
        } catch (error) {
            const apiError = error as ApiError;
            if (apiError.errorCode === 'KIOSK_OCTOPUS_CLEAR_REFUSED') {
                notify(translate('resources.kiosk-devices.octopusClear.notOpen'), { type: 'error' });
                reload();
            } else {
                notify(translate(`resources.kiosk-devices.errors.${apiError.errorCode}`, { _: translate('resources.kiosk-devices.octopusClear.cancelFailed') }), { type: 'error' });
            }
        } finally {
            setCancelling(false);
        }
    };

    return (
        <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <CreditCardOff sx={{ mr: 2, color: '#D32F2F' }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                        {translate('resources.kiosk-devices.octopusClear.title')}
                    </Typography>
                </Box>

                {rows.length > 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
                        <Typography variant="body2">{translate('resources.kiosk-devices.octopusClear.explain')}</Typography>
                        <Typography variant="body2">{translate('resources.kiosk-devices.octopusClear.evidence')}</Typography>
                        <Typography variant="body2">{translate('resources.kiosk-devices.octopusClear.chargedFollowUp')}</Typography>
                        {rows.map((row) => (
                            <Box key={row.attemptId} data-testid={`blocked-${row.attemptId}`} sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                                <Typography variant="body2">{translate('resources.kiosk-devices.octopusClear.receipt', { receipt: row.receiptNumber })}</Typography>
                                <Typography variant="body2">HK${row.amount.toFixed(2)}</Typography>
                                <Typography variant="body2">{when(row.startedAt)}</Typography>
                                <Typography variant="body2">
                                    {row.transactionId === null
                                        ? translate('resources.kiosk-devices.octopusClear.noTransaction')
                                        : translate('resources.kiosk-devices.octopusClear.transaction', { id: row.transactionId })}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                ) : (
                    <Typography variant="body2" sx={{ mb: 2 }}>{translate('resources.kiosk-devices.octopusClear.noBlocked')}</Typography>
                )}

                {pending && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Typography variant="body2">
                            {translate(blocking ? 'resources.kiosk-devices.octopusClear.waiting' : 'resources.kiosk-devices.octopusClear.stale', { time: when(pending.createdAt) })}
                        </Typography>
                        {canWrite && (
                            <Button variant="outlined" size="small" onClick={() => cancel(pending)} disabled={cancelling} sx={{ textTransform: 'none' }}>
                                {translate('resources.kiosk-devices.octopusClear.cancel')}
                            </Button>
                        )}
                    </Box>
                )}

                {canClear && (
                    <Button variant="outlined" color="error" onClick={start} sx={{ textTransform: 'none', mb: 2 }}>
                        {translate('resources.kiosk-devices.octopusClear.button')}
                    </Button>
                )}

                {clears.filter((clear) => clear !== pending).map((clear) => (
                    <Box key={clear.id} data-testid={`clear-${clear.id}`} sx={{ borderTop: '1px solid #E0E0E0', pt: 1, mt: 1 }}>
                        <Typography variant="body2">
                            {translate(`resources.kiosk-devices.octopusClear.status.${clear.status}`)} · {translate(`resources.kiosk-devices.octopusClear.finding.${clear.finding}`)} · {clear.requestedByEmail} · {when(clear.createdAt)}
                        </Typography>
                        {clear.cleared?.map((row) => (
                            <Typography key={row.attemptId} variant="caption" component="div">
                                {translate('resources.kiosk-devices.octopusClear.clearedRow', { receipt: row.receiptNumber, amount: row.amount.toFixed(2) })}
                            </Typography>
                        ))}
                        {clear.skipped?.map((row) => (
                            <Typography key={row.attemptId} variant="caption" component="div">
                                {translate(`resources.kiosk-devices.octopusClear.skipped.${row.reason}`, { attempt: row.attemptId })}
                            </Typography>
                        ))}
                        <Typography variant="caption" component="div" color="text.secondary">{clear.note}</Typography>
                    </Box>
                ))}

                <Dialog open={open} onClose={() => !saving && setOpen(false)} fullWidth maxWidth="sm">
                    <DialogTitle>{translate('resources.kiosk-devices.octopusClear.button')}</DialogTitle>
                    <DialogContent>
                        <DialogContentText sx={{ mb: 2 }}>{translate('resources.kiosk-devices.octopusClear.confirm')}</DialogContentText>
                        {rows.map((row) => (
                            <FormControlLabel
                                key={row.attemptId}
                                control={
                                    <Checkbox
                                        checked={selected.includes(row.attemptId)}
                                        onChange={(event) =>
                                            setSelected((current) => (event.target.checked ? [...current, row.attemptId] : current.filter((id) => id !== row.attemptId)))
                                        }
                                        inputProps={{ 'aria-label': row.receiptNumber }}
                                    />
                                }
                                label={translate('resources.kiosk-devices.octopusClear.receipt', { receipt: row.receiptNumber })}
                            />
                        ))}
                        <RadioGroup value={finding} onChange={(event) => setFinding(event.target.value as 'not_charged' | 'charged')}>
                            <FormControlLabel value="not_charged" control={<Radio />} label={translate('resources.kiosk-devices.octopusClear.finding.not_charged')} />
                            <FormControlLabel value="charged" control={<Radio />} label={translate('resources.kiosk-devices.octopusClear.finding.charged')} />
                        </RadioGroup>
                        {finding === 'charged' && (
                            <DialogContentText sx={{ mb: 2 }}>{translate('resources.kiosk-devices.octopusClear.chargedFollowUp')}</DialogContentText>
                        )}
                        <TextField
                            label={translate('resources.kiosk-devices.octopusClear.note')}
                            helperText={translate('resources.kiosk-devices.octopusClear.noteHelper')}
                            value={note}
                            onChange={(event) => setNote(event.target.value)}
                            inputProps={{ maxLength: 500, 'data-testid': 'clear-note' }}
                            multiline
                            minRows={3}
                            fullWidth
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setOpen(false)} disabled={saving}>{translate('ra.action.cancel')}</Button>
                        <Button variant="contained" color="error" onClick={submit} disabled={saving || selected.length === 0 || finding === '' || note.trim() === ''}>
                            {translate('resources.kiosk-devices.octopusClear.submit')}
                        </Button>
                    </DialogActions>
                </Dialog>
            </CardContent>
        </Card>
    );
};
